'use strict';
import React from 'react';
import {createEventHandler, mapPropsStream} from 'recompose';
import {Observable} from 'rxjs';
import 'rxjs/add/operator/combineLatest';
import 'rxjs/add/operator/debounceTime';
import 'rxjs/add/operator/takeUntil';
// eslint-disable-next-line max-len
import {statusPropsAreEqual} from '../../../core-components/selectors/components/StatelessStatusSelector/StatelessStatusSelectorComponent';
import {getDomainClass, getId} from '../../../core-utilities/domain/ReduxUtils';
import {ButtonState} from '../../../core-utilities/enums/ButtonState';
import {epicErrorHandler} from '../../../core-utilities/rx-utils';
import {TaskStatus} from '../../../tasks/enums/TaskStatus';
import {QuickStatusButtonView} from './QuickStatusButtonView';
import {logger} from '../../logger';


const {
  cancelled,
  pending,
  in_progress,
  submitted,
  approved,
} = TaskStatus;

// Work in progress
export const StreamedComponent = (props$) => {
  const {
    handler: onSelectSubmit,
    stream: selectSubmit$,
  } = createEventHandler();
  const {
    handler: onSelectApprove,
    stream: selectApprove$,
  } = createEventHandler();

  const state$ = props$
    .distinctUntilChanged(modelIdPropsAreEqual)
    // Reset the state if the model changes
    .switchMap(() =>
      Observable
        .merge(
          selectSubmit$.map(mapToStatus(submitted)),
          selectApprove$.map(mapToStatus(approved)),
        )
        .do(({e}) => e.stopPropagation())
        .withLatestFrom(props$)
        .takeUntil(props$.last())
        .switchMap(([{status}, props]) =>
          status === props.status ? Observable.empty() : Observable
            .merge(
              // Initialize the working state
              Observable.of({
                mode: ButtonState.WORKING,
                workingStatus: status,
              }),
              // In parallel, dispatch the update request
              Observable.fromPromise(getDomainClass(props.model).updateStatus(props.model, status))
                .switchMap(() =>
                  Observable
                    .merge(
                      // Wait for the model to be updated
                      props$
                        .map(props => props.status)
                        .distinctUntilChanged()
                        .filter(modelStatus => modelStatus !== status)
                        .mapTo(ButtonState.SUCCESS),
                      // Time out after 4 minutes
                      Observable
                        .timer(240000)
                        .mapTo(ButtonState.ERROR),
                    )
                    .take(1)
                    .switchMap(revertAfterTimeout)
                )
                .catch(() => revertAfterTimeout(ButtonState.ERROR)),
            )
        )
        .startWith({
          mode: ButtonState.START,
          workingStatus: null,
        })
        .scan((acc, curr) => ({
          ...acc,
          ...curr,
        }))
    )
    .catch(epicErrorHandler(logger, 'QuickStatusButtonContainer'));

  const statusMap$ = props$
    .distinctUntilChanged(statusPropsAreEqual)
    .map(props => props.validStatuses ? createStatusMap(props.validStatuses) : {});

  return props$
    .distinctUntilChanged()
    .combineLatest(
      state$,
      statusMap$,
      (props, state, statusMap) => {
        const mode = state.mode;
        const isWorking = mode !== ButtonState.START;
        const status = isWorking ? state.workingStatus : props.status;
        const isWorkingSubmit = isWorking && state.workingStatus === submitted;
        const isWorkingApprove = isWorking && state.workingStatus === approved;
        // determine if we *can* show the button
        const showSubmit = statusMap.submitted && (status === pending || status === in_progress);
        const showApprove = statusMap.approved && status !== cancelled && status !== approved;
        return {
          ...props,
          mode,
          // Show the button if if we can show it or its request is still in progress
          showQuickSubmit: isWorkingSubmit || showSubmit,
          showQuickApprove: isWorkingApprove || (!isWorkingSubmit && showApprove && !showSubmit),
          onSelectSubmit,
          onSelectApprove,
        };
      });
};

export const QuickStatusButtonContainer = mapPropsStream(StreamedComponent)(QuickStatusButtonView);

function mapToStatus(status) {
  return e => ({e, status});
}

function revertAfterTimeout(resultMode) {
  return Observable
    .merge(
      Observable.of({mode: resultMode}),
      Observable.timer(750).mapTo({
        mode: ButtonState.START,
        workingStatus: null,
      }),
    );
}

function modelIdPropsAreEqual(a, b) {
  if (!a && !b) {
    return true;
  } else if (!a || !b) {
    return false;
  } else {
    return getId(a.model) === getId(b.model);
  }
}

export function createStatusMap(validStatuses) {
  return validStatuses.reduce((statusMap, status) => {
    statusMap[status] = true;
    return statusMap;
  }, {});
}



// WEBPACK FOOTER //
// ./src/js/components/buttons/QuickStatusButton/QuickStatusButtonContainer.js